import { getRecommendationAutomationRegistryForPlatform } from '$extensionSrc/automations/recommendationAutomationRegistryApi'
import { StoredRecommendationProgress } from '$extensionSrc/recommendations/recommendationProgressTypes'
import { getSpecForPlatformRecommendation } from '$extensionSrc/recommendations/recommendationRegistryApi'

import {
  LogAllActionsDocument,
  LogActionInput,
  LogFullScanInput,
  InitialScanResultInput,
  SettingState,
} from '../__generated__/graphql'
import { getClient } from '../apollo'
import { PLATFORM, RecommendationKey, STORAGE_KEY } from '../utils/enums'

// Utility functions for extracting data

export function getSettingsSelectedFromProgress(
  progress: StoredRecommendationProgress,
): string[] {
  const settingsSelectedMap: { [k: string]: boolean } =
    progress?.settingsSelected || {}
  return Object.entries(settingsSelectedMap)
    .filter(([, selected]) => selected)
    .map(([key]) => key)
}

export function getNUnitsUpdatedFromProgress(
  progress: StoredRecommendationProgress,
): number | undefined {
  return progress?.statistics?.lastUpdatedCount
}

/**
 * Given the progress for a recommendation, determine if its settings are
 * fulfilled, disabled, or just incomplete.
 *
 * Used to format the input as the GraphQL API expects.
 */
export function getScanResults(
  platform: PLATFORM,
  recommendationKey: RecommendationKey,
  progress: StoredRecommendationProgress,
): InitialScanResultInput[] {
  if (!progress) {
    return []
  }

  const spec = getSpecForPlatformRecommendation(platform, recommendationKey)
  const autoDef =
    getRecommendationAutomationRegistryForPlatform(platform)[recommendationKey]

  if (!spec.settings) {
    return []
  }

  return spec.settings.map(({ key: sKey }) => {
    const isDisabled = autoDef.getIsDisabledFn(sKey)(progress.automationErrors)
    const isFulfilled = autoDef.getIsFulfilledFn(sKey)(progress.initialState)
    let settingState = SettingState.Incomplete
    if (isDisabled) {
      settingState = SettingState.Disabled
    }
    if (isFulfilled) {
      settingState = SettingState.Fulfilled
    }
    return {
      recommendationKey,
      settingKey: sKey,
      settingState,
    }
  })
}

export async function addUserActionToLog(
  action: Omit<LogActionInput, 'completedOn'>,
): Promise<void> {
  const completedOn = new Date().toISOString()
  const storageKey = STORAGE_KEY.USER_ACTION_LOG

  // Get existing logs
  const result = await chrome.storage.local.get(storageKey)
  const existingLogs =
    (result[storageKey] as LogActionInput[] | undefined) || []

  // Add new action with timestamp
  await chrome.storage.local.set({
    [storageKey]: [...existingLogs, { ...action, completedOn }],
  })
}

export async function addUserFullScanToLog(
  action: Omit<LogFullScanInput, 'completedOn'>,
): Promise<void> {
  const completedOn = new Date().toISOString()
  const storageKey = STORAGE_KEY.USER_ACTION_LOG

  // Get existing logs
  const result = await chrome.storage.local.get(storageKey)
  const existingLogs =
    (result[storageKey] as LogFullScanInput[] | undefined) || []

  // Add new action with timestamp
  await chrome.storage.local.set({
    [storageKey]: [...existingLogs, { ...action, completedOn }],
  })
}

function isLogActionInput(action: LogActionInput | LogFullScanInput) {
  return 'actionType' in action
}

function isLogFullScanInput(action: LogActionInput | LogFullScanInput) {
  return 'scanResults' in action
}

export async function sendUserActionsToServer(): Promise<void> {
  const storageKey = STORAGE_KEY.USER_ACTION_LOG

  // Get all pending actions
  const result = await chrome.storage.local.get(storageKey)
  const pendingActions: (LogActionInput | LogFullScanInput)[] =
    (result[storageKey] as (LogActionInput | LogFullScanInput)[] | undefined) ||
    []

  if (pendingActions.length === 0) {
    return // No actions to send
  }

  try {
    const { errors } = await getClient().mutate({
      mutation: LogAllActionsDocument,
      variables: {
        logActionInputs: pendingActions.filter(isLogActionInput),
        logFullScanInputs: pendingActions.filter(isLogFullScanInput),
      },
    })

    if (!errors || errors.length === 0) {
      // Clear the log if no GQL errors (e.g, server received response, and
      // there were no schema issues - the server may have failed to process
      // the response, but it got to the server logic)
      await chrome.storage.local.remove(storageKey)
    }
  } catch (_error) {
    // Don't throw, we'll retry on the next storage change
  }
}

export async function clearUserActionLog(): Promise<void> {
  await chrome.storage.local.remove(STORAGE_KEY.USER_ACTION_LOG)
}
